import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  InputGroup,
  Button,
  Spinner,
} from "react-bootstrap";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { FaSearch, FaFileExcel } from "react-icons/fa";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";

function TruckTerminus() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  //   const API_URL = process.env.REACT_APP_API_URL_TERMINUS;

  const fetchData = async (start = "", end = "") => {
    setLoading(true);
    try {
      let url = `https://terminus.balurghatmnc.in/admin/search-api.php`;

      // Remove server-side date filtering - we'll do it client-side
      const response = await axios.get(url);

      if (response.data.status === "success") {
        setData(response.data.records || []);
      } else {
        setData([]);
        toast.warn("No data received from the server.");
      }
    } catch (error) {
      toast.error("An error occurred while fetching the truck terminus data.");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilter = () => {
    if (startDate && endDate) {
      // Client-side filtering - no need to call API
      toast.success("Date filter applied successfully!");
    } else {
      toast.warn("Please select both start and end date for filtering.");
    }
  };

  const handleClearFilter = () => {
    setStartDate("");
    setEndDate("");
    toast.info("Date filter cleared. Showing all data.");
  };

  const filteredData = useMemo(() => {
    // Start with all data
    let filtered = data;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((item) => {
        const search = searchTerm.toLowerCase();
        return (
          (item.Entry_No && item.Entry_No.toLowerCase().includes(search)) ||
          (item.Vehicle_No && item.Vehicle_No.toLowerCase().includes(search)) ||
          (item.Customer_Name &&
            item.Customer_Name.toLowerCase().includes(search)) ||
          (item.Vehicle_Type &&
            item.Vehicle_Type.toLowerCase().includes(search))
        );
      });
    }

    // Apply date filter (client-side)
    if (startDate && endDate) {
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.Entry_Date);
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Set time to start of day for start date and end of day for end date
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        return itemDate >= start && itemDate <= end;
      });
    }

    // Sort by Entry_Date in descending order (newest first)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.Entry_Date);
      const dateB = new Date(b.Entry_Date);
      return dateB - dateA;
    });
  }, [data, searchTerm, startDate, endDate]);

  // Calculate total amount from filtered data
  const totalAmount = useMemo(() => {
    return filteredData.reduce((sum, item) => {
      const amount = parseFloat(item.Total_Amount) || 0;
      return sum + amount;
    }, 0);
  }, [filteredData]);

  const handleExport = () => {
    if (filteredData.length === 0) {
      toast.warn("No data to export.");
      return;
    }
    
    // Calculate total amount for export
    const exportTotalAmount = filteredData.reduce((sum, item) => {
      const amount = parseFloat(item.Total_Amount) || 0;
      return sum + amount;
    }, 0);
    
    const dataToExport = filteredData.map((item) => ({
      "Sl No": item.Sl_No,
      "Entry No": item.Entry_No,
      "Vehicle No": item.Vehicle_No,
      "Vehicle Type": item.Vehicle_Type,
      "Customer Name": item.Customer_Name || "N/A",
      "Driver Phone": item.Driver_Phone || "N/A",
      "Entry Date": item.Entry_Date,
      "Entry Time": item.Entry_Time,
      "Exit Date/Time": item.ExitDatetime || "N/A",
      "Total Amount (₹)": parseFloat(item.Total_Amount) || 0,
    }));
    
    // Add total row at the end with "Total" text
    const totalRow = {
      "Sl No": "",
      "Entry No": "",
      "Vehicle No": "",
      "Vehicle Type": "",
      "Customer Name": "",
      "Driver Phone": "",
      "Entry Date": "",
      "Entry Time": "",
      "Exit Date/Time": "",
      "Total Amount (₹)":`Total: ₹${exportTotalAmount.toLocaleString('en-IN')}`,
    };
    
    dataToExport.push(totalRow);
    
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    
    // Apply bold formatting to the total row
    const totalRowIndex = dataToExport.length - 1;
    const lastColumnIndex = Object.keys(dataToExport[0]).length - 1;
    
    // Make the total amount cell bold
    const cellRef = XLSX.utils.encode_cell({ r: totalRowIndex, c: lastColumnIndex });
    if (!worksheet['!cols']) worksheet['!cols'] = [];
    if (!worksheet['!rows']) worksheet['!rows'] = [];
    
    // Set bold formatting for the total amount cell
    if (!worksheet[cellRef]) worksheet[cellRef] = {};
    worksheet[cellRef].s = { font: { bold: true } };
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "TruckTerminusReport");
    XLSX.writeFile(workbook, "Truck_Terminus_Report.xlsx");
    
    toast.success(`Exported ${filteredData.length} records with total amount: ₹${exportTotalAmount.toLocaleString('en-IN')}`);
  };

  const columns = [
    {
      name: "Sl No",
      selector: (row) => row.Sl_No,
      sortable: true,
      width: "80px",
      minWidth: "80px",
      maxWidth: "100px",
    },
    {
      name: "Entry No",
      selector: (row) => row.Entry_No,
      sortable: true,
      width: "15%",
      minWidth: "120px",
      cell: (row) => <div title={row.Entry_No}>{row.Entry_No}</div>,
    },
    {
      name: "Vehicle No",
      selector: (row) => row.Vehicle_No,
      sortable: true,
      width: "12%",
      minWidth: "100px",
      cell: (row) => <div title={row.Vehicle_No}>{row.Vehicle_No}</div>,
    },
    {
      name: "Vehicle Type",
      selector: (row) => row.Vehicle_Type,
      sortable: true,
      width: "10%",
      minWidth: "90px",
      cell: (row) => (
        <span className="badge bg-info text-dark">{row.Vehicle_Type}</span>
      ),
    },
    {
      name: "Customer Name",
      selector: (row) => row.Customer_Name || "N/A",
      sortable: true,
      width: "15%",
      minWidth: "120px",
      cell: (row) => (
        <div title={row.Customer_Name || "N/A"}>
          {row.Customer_Name || "N/A"}
        </div>
      ),
    },
    {
      name: "Driver Phone",
      selector: (row) => row.Driver_Phone || "N/A",
      sortable: true,
      width: "12%",
      minWidth: "100px",
      cell: (row) => (
        <div title={row.Driver_Phone || "N/A"}>{row.Driver_Phone || "N/A"}</div>
      ),
    },
    {
      name: "Entry Date",
      selector: (row) => row.Entry_Date,
      sortable: true,
      width: "10%",
      minWidth: "100px",
    },
    {
      name: "Entry Time",
      selector: (row) => row.Entry_Time,
      sortable: true,
      width: "10%",
      minWidth: "90px",
    },
    {
      name: "Exit Date/Time",
      selector: (row) => row.ExitDatetime || "N/A",
      sortable: true,
      width: "12%",
      minWidth: "120px",
      cell: (row) => (
        <div title={row.ExitDatetime || "N/A"}>{row.ExitDatetime || "N/A"}</div>
      ),
    },
    {
      name: "Total Amount (₹)",
      selector: (row) => row.Total_Amount,
      sortable: true,
      width: "12%",
      minWidth: "100px",
      right: true,
      cell: (row) => (
        <span className="fw-bold text-success">₹{row.Total_Amount}</span>
      ),
    },
  ];

  const customStyles = {
    rows: {
      style: {
        fontSize: "14px",
        minHeight: "50px",
        "&:hover": {
          backgroundColor: "#f8f9fa",
        },
      },
    },
    headCells: {
      style: {
        fontSize: "15px",
        fontWeight: "bold",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #dee2e6",
        padding: "12px 8px",
        textAlign: "left",
        whiteSpace: "nowrap",
      },
    },
    cells: {
      style: {
        padding: "12px 8px",
        borderBottom: "1px solid #dee2e6",
        whiteSpace: "nowrap",
      },
    },
    table: {
      style: {
        minWidth: "100%",
      },
    },
  };

  return (
    <>
      <Topbar />
      <div className="d-flex">
        <Sidebar />
        <Container fluid className="p-4 bg-light">
          <Card className="shadow-sm">
            <Card.Header
              as="h5"
              style={{
                backgroundColor: "#e3f2fd",
                borderBottom: "2px solid #1976d2",
              }}
            >
              <span style={{ color: "#1976d2" }}>Truck Terminus Report</span>
            </Card.Header>
            <Card.Body>
              <div className="p-3 border-bottom bg-light rounded mb-4">
                <Row className="g-3 align-items-end">
                  <Col md={4}>
                    <Form.Label>Search</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaSearch />
                      </InputGroup.Text>
                      <Form.Control
                        type="search"
                        placeholder="Search by Entry No, Vehicle No, Customer Name, or Vehicle Type..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Col>
                  <Col md={2} className="text-end d-flex gap-2">
                    <Button
                      onClick={handleFilter}
                      variant="primary"
                      className=""
                    >
                      Filter
                    </Button>
                    <Button
                      onClick={handleClearFilter}
                      variant="secondary"
                      className="w-100"
                    >
                      Clear
                    </Button>
                    <Button onClick={handleExport} variant="success">
                      <FaFileExcel />
                    </Button>
                  </Col>
                </Row>
              </div>
              <div
                className="table-responsive"
                style={{ overflowX: "auto", overflowY: "hidden" }}
              >
                <DataTable
                  columns={columns}
                  data={filteredData}
                  pagination
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[10, 25, 50, 100]}
                  responsive
                  highlightOnHover
                  customStyles={customStyles}
                  progressPending={loading}
                  progressComponent={
                    <div className="p-5 text-center">
                      <Spinner animation="border" variant="primary" />
                    </div>
                  }
                  noDataComponent={
                    <div className="p-5 text-center text-muted">
                      <i className="fas fa-database fa-3x mb-3"></i>
                      <p>No data available</p>
                    </div>
                  }
                />
              </div>

              {/* Total Row */}
              {filteredData.length > 0 && (
                <div className="mt-3 p-3 bg-primary rounded">
                  <Row className="align-items-center">
                    <Col md={6}>
                      <h6 className="mb-0">
                        <i className="fas fa-calculator me-2"></i>
                        Total Records:{" "}
                        <span className="badge bg-light text-dark ms-2">
                          {filteredData.length}
                        </span>
                      </h6>
                    </Col>
                    <Col md={6} className="text-end">
                      <h5 className="mb-0">
                        <i className="fas fa-rupee-sign me-2"></i>
                        Total Amount:{" "}
                        <span className="badge bg-success fs-6 ms-2">
                          ₹{totalAmount.toLocaleString("en-IN")}
                        </span>
                      </h5>
                    </Col>
                  </Row>
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  );
}

export default TruckTerminus;
